import {
  UsersIcon,
  ChartBarIcon,
  RectangleStackIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline';
import { AdminRolesEnum } from '@/dashboard/enums/admin-roles';

export interface AdminNavItem {
  name: string;
  href: string;
  roles: string[];
}

export interface AdminNavGroup {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  items: AdminNavItem[];
  defaultOpen?: boolean;
}

export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
  {
    title: 'Platform',
    icon: UsersIcon,
    defaultOpen: true,
    items: [
      {
        name: 'Stores',
        href: '/admin/merchants',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.SUPPORT],
      },
      {
        name: 'Customers',
        href: '/admin/merchants-usage',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.SUPPORT],
      },
      {
        name: 'Users',
        href: '/admin/users',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.SUPPORT],
      },
      {
        name: 'Pages',
        href: '/admin/pages',
        roles: [
          AdminRolesEnum.ADMIN,
          AdminRolesEnum.SUPPORT,
          AdminRolesEnum.EDITOR,
          AdminRolesEnum.DEVELOPER,
        ],
      },
      {
        name: 'Payments',
        href: '/admin/payments',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.SUPPORT],
      },
      {
        name: 'Bookings',
        href: '/admin/bookings',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.SUPPORT],
      },
      {
        name: 'Subscriptions',
        href: '/admin/subscriptions',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.SUPPORT],
      },
      {
        name: 'Submissions',
        href: '/admin/submissions',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.SUPPORT],
      },
    ],
  },
  {
    title: 'Usage',
    icon: ChartBarIcon,
    items: [
      {
        name: 'Page prompts',
        href: '/admin/page-prompts',
        roles: [
          AdminRolesEnum.ADMIN,
          AdminRolesEnum.SUPPORT,
          AdminRolesEnum.EDITOR,
          AdminRolesEnum.DEVELOPER,
        ],
      },
      {
        name: 'AI usage',
        href: '/admin/ai-usage',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.SUPPORT, AdminRolesEnum.DEVELOPER],
      },
      {
        name: 'Link in bio',
        href: '/admin/link-in-bio',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.SUPPORT, AdminRolesEnum.DEVELOPER],
      },
      {
        name: 'Help center search intents',
        href: '/admin/help-center-search-intents',
        roles: [
          AdminRolesEnum.ADMIN,
          AdminRolesEnum.SUPPORT,
          AdminRolesEnum.EDITOR,
          AdminRolesEnum.DEVELOPER,
        ],
      },
      {
        name: 'Themes',
        href: '/admin/themes',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.EDITOR, AdminRolesEnum.DEVELOPER],
      },
      {
        name: 'API keys',
        href: '/admin/api-keys',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.DEVELOPER],
      },
      {
        name: 'OAuth tokens',
        href: '/admin/oauth-tokens',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.DEVELOPER],
      },
      {
        name: 'Webhooks',
        href: '/admin/webhooks',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.DEVELOPER],
      },
      {
        name: 'Plans',
        href: '/admin/plans',
        roles: [AdminRolesEnum.ADMIN],
      },
    ],
  },
  {
    title: 'Content',
    icon: RectangleStackIcon,
    items: [
      {
        name: 'Templates',
        href: '/admin/templates',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.EDITOR, AdminRolesEnum.DEVELOPER],
      },
      {
        name: 'Template categories',
        href: '/admin/template-categories',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.EDITOR, AdminRolesEnum.DEVELOPER],
      },
    ],
  },
  {
    title: 'Developer',
    icon: CodeBracketIcon,
    items: [
      {
        name: 'Actions',
        href: '/admin/actions',
        roles: [AdminRolesEnum.ADMIN],
      },
      {
        name: 'Statistic logs',
        href: '/admin/statistic-logs',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.DEVELOPER],
      },
      {
        name: 'Metric logs',
        href: '/admin/metric-logs',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.DEVELOPER],
      },
      {
        name: 'Integration logs',
        href: '/admin/integration-logs',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.DEVELOPER],
      },
      {
        name: 'Calculate page metadata',
        href: '/admin/calculate-page-metadata',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.DEVELOPER],
      },
      {
        name: 'Job queues',
        href: '/admin/queues',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.DEVELOPER],
      },
      {
        name: 'Email templates',
        href: '/admin/email-templates',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.DEVELOPER],
      },
      {
        name: 'Brand fetches',
        href: '/admin/brand-fetches',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.DEVELOPER],
      },
      {
        name: 'Page compare',
        href: '/admin/pages/compare',
        roles: [AdminRolesEnum.ADMIN, AdminRolesEnum.DEVELOPER],
      },
    ],
  },
];

/**
 * Filters navigation groups based on user role.
 * Returns only groups that have at least one accessible item for the given role.
 * Items within each group are also filtered to only include accessible ones.
 */
export function getFilteredNavGroups(role: string): AdminNavGroup[] {
  return ADMIN_NAV_GROUPS.map(group => ({
    ...group,
    items: group.items.filter(item => item.roles.includes(role)),
  })).filter(group => group.items.length > 0);
}

/**
 * Checks if a given path is within any of the navigation items.
 * Used to determine which group should be expanded by default.
 */
export function isPathInGroup(pathname: string, group: AdminNavGroup): boolean {
  return group.items.some(item => pathname.startsWith(item.href));
}
